import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, Alert, CircularProgress,
  Divider, FormControl, InputLabel, Select, MenuItem,
  IconButton, Chip, InputAdornment, Tooltip, useTheme, useMediaQuery,
  Portal, Slide, alpha, createTheme, ThemeProvider, GlobalStyles,
  Checkbox, FormControlLabel, Paper, Avatar,
} from '@mui/material';
import {
  PersonAdd, Person, Close, Send, ContentCopy, Wifi, Schedule,
  Visibility, VisibilityOff, AttachMoney, Search,
  Minimize as MinimizeIcon, OpenInFull as OpenInFullIcon,
  FullscreenExit as FullscreenExitIcon, Fullscreen as FullscreenIcon,
  AttachFile, CheckCircle, Cancel, OpenInNew, Save,
  Description, Draw, PhotoCamera, FilePresent, Download, InsertDriveFile,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import PlanosSelect from '../selects/PlanosSelect';
import FuncionariosSelect from '../selects/FuncionariosSelect';
import EscritorioSelect from '../selects/EscritorioSelect';
import ClientSelect from '../selects/ClientSelect';
import NasSelect from '../selects/NasSelect';
import CfopSelect from '../selects/CfopSelect';
import DiasVencimentoSelect from '../selects/DiasVencimentoSelect';
import EstadosBrSelect from '../selects/EstadosBrSelect';
import templateService, { Template } from '../../services/templateService';
import { useAuth } from '../../contexts/AuthContext';
import { planosService } from '../../services/planosService';
import preCadastroService, { PreCadastroAtendente, CriarPreCadastroPayload } from '../../services/preCadastroService';
import ContratoServicosService from '../../services/contratoServicosService';
import { buscarCep, formatarCepOnInput, formatarCPFCNPJOnInput, formatarPhoneOnInput, formatarCPFOnInput, formatarCNPJOnInput } from '../../utils/formatToView';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3001').replace(/\/api$/, '');

const STATUS_CONFIG: Record<
  string,
  { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }
> = {
  aguardando_assinatura: { label: 'Aguardando Assinatura', color: 'info' },
  aguardando_aprovacao: { label: 'Aguardando Aprovação', color: 'warning' },
  pendente: { label: 'Pendente', color: 'warning' },
  aprovado: { label: 'Aprovado', color: 'success' },
  reprovado: { label: 'Reprovado', color: 'error' },
  expirado: { label: 'Expirado', color: 'default' },
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'default' as const };
  return (
    <Chip
      label={cfg.label}
      color={cfg.color}
      size="small"
      sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
    />
  );
};

interface PreCadastroFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (data?: PreCadastroAtendente) => void;
  preCadastroId?: number | null;
  id?: number | null;
  initialData?: PreCadastroAtendente | null;
  editingItem?: PreCadastroAtendente | null;
  preCadastro?: PreCadastroAtendente | null;
  onApprove?: (id: number) => Promise<void>;
  onReject?: (id: number) => Promise<void>;
  minimized?: boolean;
  onMinimizedChange?: (minimized: boolean) => void;
  stackIndex?: number;
  zIndex?: number;
  ticketId?: number;
}

const TIPO_CONEXAO_OPTIONS = [
  { value: 'fibra', label: 'Fibra Óptica' },
  { value: 'radio', label: 'Rádio' },
  { value: 'cabo_coaxial', label: 'Cabo Coaxial' },
  { value: 'par_metalico', label: 'Par Metálico' },
  { value: 'satelite', label: 'Satélite' },
];

const TIPO_CLIENTE_OPTIONS = [
  { value: '01', label: '01 - Comercial' },
  { value: '02', label: '02 - Industrial' },
  { value: '03', label: '03 - Residencial/Pessoa Física' },
  { value: '04', label: '04 - Produtor Rural' },
  { value: '05', label: '05 - Órgão da administração pública estadual ICMS 107/95' },
  { value: '06', label: '06 - Prestador de serviço de telecomunicação ICMS 17/13' },
  { value: '07', label: '07 - Missões Diplomáticas ICMS 158/94' },
  { value: '08', label: '08 - Igrejas e Templos de qualquer natureza' },
  { value: '99', label: '99 - Outros' },
];

const COMO_CONHECEU_OPTIONS = [
  'Carro da empresa',
  'Facebook',
  'Google',
  'Indicação de amigo',
  'Instagram',
  'Outdoor',
  'Outro',
  'Panfleto',
  'Passou em frente à loja',
  'Rádio',
  'Site',
  'TikTok',
  'Whatsapp',
];

const CONTRIBUINTE_ICMS_OPTIONS = [
  { value: 1, label: '1 - Contribuinte ICMS' },
  { value: 2, label: '2 - Contribuinte isento de Inscrição no cadastro de Contribuintes do ICMS' },
  { value: 9, label: '9 - Não contribuinte ICMS' },
];

// Funções de formatação de moeda idênticas ao TabbedContractForm
const formatCurrencyInput = (value: string): string => {
  const numericValue = value.replace(/\D/g, '');
  if (!numericValue) return '';
  const cents = parseInt(numericValue, 10);
  const reais = cents / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(reais);
};

const parseCurrencyInput = (formattedValue: string): number => {
  const numericString = formattedValue
    .replace(/[R$\s.]/g, '')
    .replace(',', '.');
  return parseFloat(numericString) || 0;
};

const formatCurrencyDisplay = (val?: number | null): string => {
  if (val === undefined || val === null) return '';
  if (val === 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(val);
};

const MINIMIZED_BAR_HEIGHT = 56;

const PreCadastroForm: React.FC<PreCadastroFormProps> = ({
  open,
  onClose,
  onSuccess,
  preCadastroId,
  id,
  initialData,
  editingItem,
  preCadastro,
  onApprove,
  onReject,
  minimized: controlledMinimized,
  onMinimizedChange,
  stackIndex = 0,
  zIndex,
  ticketId,
}) => {
  const effectiveId = preCadastroId ?? id ?? initialData?.id ?? editingItem?.id ?? preCadastro?.id;
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [fetchedData, setFetchedData] = useState<PreCadastroAtendente | null>(null);

  const targetData = fetchedData || initialData || editingItem || preCadastro;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLink, setShowLink] = useState<string | null>(null);
  const [showPppoe, setShowPppoe] = useState(false);
  const [internalMinimized, setInternalMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);

  const isMinimized = controlledMinimized !== undefined ? controlledMinimized : internalMinimized;
  const setIsMinimized = (val: boolean) => {
    if (onMinimizedChange) {
      onMinimizedChange(val);
    } else {
      setInternalMinimized(val);
    }
  };

  // Pelo padrão do MUI, um Dialog tem zIndex 1300. Se atribuirmos sx={{ zIndex: resolvedZIndex }} diretamente
  // na raiz do Dialog, e os Menus/Popovers forem renderizados num Portal no <body>, eles usam o zIndex do tema padrão (1300).
  // Com baseZIndex alto e ThemeProvider envolvendo os Menus ou sem conflito de sx no Dialog, os selects sempre abrem na frente.
  const dialogZIndex = zIndex ?? theme.zIndex.modal + stackIndex + 1;
  const popoverZIndex = dialogZIndex + 100;

  const embeddedTheme = useMemo(() => {
    return createTheme(theme, {
      zIndex: {
        ...theme.zIndex,
        modal: dialogZIndex,
        popover: popoverZIndex,
        tooltip: popoverZIndex + 50,
      },
      components: {
        ...theme.components,
        MuiModal: {
          styleOverrides: {
            root: {
              zIndex: `${dialogZIndex} !important`,
            },
          },
        },
        MuiPopover: {
          styleOverrides: {
            root: {
              zIndex: `${popoverZIndex} !important`,
            },
            paper: {
              zIndex: `${popoverZIndex} !important`,
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            root: {
              zIndex: `${popoverZIndex} !important`,
            },
            paper: {
              zIndex: `${popoverZIndex} !important`,
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            popper: {
              zIndex: `${popoverZIndex} !important`,
            },
          },
        },
        MuiSelect: {
          defaultProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  zIndex: `${popoverZIndex} !important`,
                },
              },
              sx: {
                zIndex: `${popoverZIndex} !important`,
              },
            },
          },
        },
      },
    });
  }, [theme, dialogZIndex, popoverZIndex]);

  const [form, setForm] = useState<Partial<CriarPreCadastroPayload>>({
    modalidade: undefined,
    fidelidade: 12,
    taxa_instalacao: 0,
    tipo_pessoa: 'pf',
  });

  const [contratoTemplates, setContratoTemplates] = useState<Template[]>([]);
  const [contratoTemplatesLoading, setContratoTemplatesLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const loadTemplates = async () => {
        setContratoTemplatesLoading(true);
        try {
          const response = await templateService.getTemplates({
            type: 'contrato',
            isActive: true,
            limit: 100,
          });
          setContratoTemplates(response.templates || []);
        } catch (err) {
          console.error('Erro ao carregar templates de contrato:', err);
          setContratoTemplates([]);
        } finally {
          setContratoTemplatesLoading(false);
        }
      };
      loadTemplates();
    }
  }, [open]);

  const [preenchidoPeloCliente, setPreenchidoPeloCliente] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [cepBuscado, setCepBuscado] = useState(false);
  const [pppoeChecking, setPppoeChecking] = useState(false);
  const [pppoeError, setPppoeError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (effectiveId) {
        setFetchingDetails(true);
        setError(null);
        setShowLink(null);
        setPreenchidoPeloCliente(false);
        setPppoeError(null);

        preCadastroService.obter(effectiveId)
          .then((data) => {
            setFetchedData(data);
            setForm({
              cfop: '5307',
              ...data,
              data_nascimento: data.data_nascimento ? String(data.data_nascimento).slice(0, 10) : undefined,
            });
            const hasAddress = Boolean(data.cep || data.cidade || data.endereco);
            setCepBuscado(hasAddress);
          })
          .catch((err: any) => {
            console.error('Erro ao buscar pré-cadastro:', err);
            setError(err.message || 'Erro ao carregar dados do pré-cadastro');
          })
          .finally(() => {
            setFetchingDetails(false);
          });
      } else if (user) {
        setFetchedData(null);
        const escritorioId = (() => {
          let ids: number[] = [];
          if (typeof user.escritorio_ids === 'string') {
            try { ids = JSON.parse(user.escritorio_ids); } catch { ids = []; }
          } else if (Array.isArray(user.escritorio_ids)) {
            ids = user.escritorio_ids;
          } else if (user.escritorio_ids) {
            ids = [user.escritorio_ids as number];
          }
          return ids[0];
        })();

        setForm({
          modalidade: undefined,
          fidelidade: 12,
          taxa_instalacao: 0,
          dia_vencimento: undefined,
          tipo_conexao: undefined,
          tipo_pessoa: 'pf',
          tipo_cliente: '03',
          contribuinte_icms: 9,
          como_conheceu: '',
          escritorio_id: escritorioId,
          vendedor_id: user.funcionario_id ?? undefined,
          cfop: '5307',
          titulo: '',
          contrato_template: undefined,
        });
        setError(null);
        setShowLink(null);
        setPreenchidoPeloCliente(true);
        setCepBuscado(false);
        setPppoeError(null);
      }
    } else {
      setFetchedData(null);
      setFetchingDetails(false);
      setCepBuscado(false);
      setPppoeError(null);
    }
  }, [open, effectiveId, user]);

  const set = (key: keyof CriarPreCadastroPayload, value: any) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleBuscarCep = async (cepValue?: string) => {
    const targetCep = cepValue ?? form.cep ?? '';
    let success = false;
    if (!targetCep || targetCep.replace(/\D/g, '').length < 8) {
      setError('Digite um CEP válido para buscar');
      return;
    }
    await buscarCep(
      targetCep,
      (field: any, val: any) => {
        set(field as keyof CriarPreCadastroPayload, val);
        success = true;
      },
      (err: string | null) => {
        if (err) setError(err);
      },
      setCepLoading
    );
    if (success) {
      setCepBuscado(true);
    }
  };

  const handleCheckPppoeUsuario = async () => {
    const usuario = form.pppoe_usuario?.trim();
    if (!usuario) {
      setPppoeError(null);
      return;
    }
    try {
      setPppoeChecking(true);
      setPppoeError(null);
      const res = await ContratoServicosService.checkUnique({ usuario_pppoe: usuario });
      if (res.data && !res.data.unique) {
        setPppoeError(res.data.message || 'Usuário PPPoE já cadastrado no sistema');
      } else {
        setPppoeError(null);
      }
    } catch (err: any) {
      console.error('Erro ao verificar usuário PPPoE:', err);
    } finally {
      setPppoeChecking(false);
    }
  };

  const validateForm = (): boolean => {
    if (pppoeError) { setError(pppoeError); return false; }
    // ════════ DADOS DO CONTRATO (OBRIGATÓRIOS SEMPRE) ════════
    if (!form.escritorio_id) { setError('Escritório é obrigatório'); return false; }
    if (!form.titulo?.trim()) { setError('Título do Contrato é obrigatório'); return false; }
    if (!form.contrato_template) { setError('Modelo de Contrato é obrigatório'); return false; }
    if (!form.tipo_pessoa) { setError('Tipo de pessoa é obrigatório'); return false; }
    if (!form.tipo_cliente) { setError('Tipo de cliente é obrigatório'); return false; }
    if (form.contribuinte_icms === undefined || form.contribuinte_icms === null) { setError('Contribuinte ICMS é obrigatório'); return false; }
    if (!form.como_conheceu) { setError('Como conheceu a Empresa? é obrigatório'); return false; }
    if (form.como_conheceu === 'Indicação de amigo' && !form.indicado_por_id) { setError('Cliente que indicou é obrigatório quando a opção é Indicação de amigo'); return false; }
    if (!form.vendedor_id) { setError('Vendedor é obrigatório'); return false; }
    if (!form.cfop) { setError('CFOP é obrigatório'); return false; }
    if (!form.plano_id) { setError('Plano é obrigatório'); return false; }
    if (!form.modalidade) { setError('Modalidade é obrigatória'); return false; }
    if (form.fidelidade === undefined || form.fidelidade === null) { setError('Fidelidade é obrigatória'); return false; }
    if (form.taxa_instalacao === undefined || form.taxa_instalacao === null) { setError('Taxa de Instalação é obrigatória'); return false; }
    if (form.valor_contratado === undefined || form.valor_contratado === null || form.valor_contratado <= 0) { setError('Valor Contratado é obrigatório'); return false; }
    if (!form.tipo_conexao) { setError('Tipo de Conexão é obrigatório'); return false; }
    if (!form.nas_id) { setError('Servidor NAS é obrigatório'); return false; }
    if (!form.pppoe_usuario?.trim()) { setError('Login PPPoE é obrigatório'); return false; }
    if (!form.pppoe_senha?.trim()) { setError('Senha PPPoE é obrigatória'); return false; }

    // ════════ DADOS DO CLIENTE (SE NÃO PREENCHIDO PELO CLIENTE) ════════
    if (!preenchidoPeloCliente) {
      if (!form.nome?.trim()) { setError('Nome Completo / Razão Social é obrigatório'); return false; }
      if (!form.cpf_cnpj?.trim()) { setError('CPF / CNPJ é obrigatório'); return false; }
      if (!form.telefone?.trim()) { setError('Telefone / Celular (WhatsApp) é obrigatório'); return false; }
      if (form.tipo_pessoa === 'pf' && !form.data_nascimento) { setError('Data de nascimento é obrigatória para Pessoa Física'); return false; }
      if (!form.cep?.trim()) { setError('CEP é obrigatório'); return false; }
      if (!form.endereco?.trim()) { setError('Endereço é obrigatório'); return false; }
      if (!form.bairro?.trim()) { setError('Bairro é obrigatório'); return false; }
      if (!form.cidade?.trim()) { setError('Cidade é obrigatória'); return false; }
      if (!form.estado?.trim()) { setError('UF / Estado é obrigatório'); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    try {
      const result = await preCadastroService.criar(form as CriarPreCadastroPayload);
      const link = result.link_cliente || '';
      setShowLink(link);
      onSuccess(result);

      if (ticketId) {
        // Fluxo específico de Atendimento (ContatoDetalhes)
        const textoMensagem = `👋 Olá! Seu cadastro está quase pronto.\n\nClique no link abaixo para preencher seus dados e concluir.\n🔗${link}`;

        window.dispatchEvent(
          new CustomEvent('sysup:preencher-mensagem-chat', {
            detail: {
              texto: textoMensagem,
              ticketId,
            },
          })
        );

        if (link) {
          navigator.clipboard.writeText(textoMensagem).catch(() => { });
        }

        toast.success('Pré-cadastro criado e link inserido na conversa!');
        onClose();
      } else {
        // Fluxo padrão geral (PreCadastrosPage etc.)
        Swal.fire({
          title: 'Pré-Cadastro Criado!',
          html: `
            <div style="text-align: left; font-size: 0.9rem; color: #374151; margin-bottom: 12px;">
              Envie este link para o cliente preencher os dados cadastrais e enviar os documentos:
            </div>
            <div style="display: flex; gap: 8px; align-items: center; background: #f3f4f6; padding: 10px; border-radius: 8px; border: 1px solid #e5e7eb;">
              <input id="swal-precadastro-link" type="text" readonly value="${link}" 
                style="width: 100%; border: none; background: transparent; font-family: monospace; font-size: 0.85rem; font-weight: 600; color: #1f2937; outline: none; cursor: pointer;"
                onclick="this.select(); navigator.clipboard.writeText('${link}'); document.getElementById('swal-copied-badge').style.display='inline-block';" 
              />
            </div>
            <div id="swal-copied-badge" style="display: none; margin-top: 6px; color: #059669; font-size: 0.8rem; font-weight: 600;">
              ✓ Link copiado para a área de transferência!
            </div>
          `,
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: '📋 Copiar Link',
          cancelButtonText: 'Fechar',
          confirmButtonColor: '#0151C4',
          cancelButtonColor: '#6b7280',
          focusConfirm: true,
          didOpen: () => {
            const input = document.getElementById('swal-precadastro-link') as HTMLInputElement | null;
            if (input) {
              input.focus();
              input.select();
            }
          },
        }).then((res) => {
          if (res.isConfirmed) {
            navigator.clipboard.writeText(link);
            toast.success('Link copiado para a área de transferência!');
          }
        });
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao criar pré-cadastro');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!targetData?.id) return;
    if (!validateForm()) return;
    setLoading(true);
    setError(null);
    try {
      await preCadastroService.atualizar(targetData.id, form);
      toast.success('Pré-cadastro atualizado com sucesso!');
      onSuccess();
    } catch (err: any) {
      const msg = err.message || 'Erro ao atualizar pré-cadastro';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveWithValidation = async () => {
    if (!targetData?.id || !onApprove) return;
    if (!validateForm()) return;
    if (!form.dia_vencimento) {
      setError('Dia de vencimento é obrigatório para aprovação');
      toast.error('Dia de vencimento é obrigatório para aprovação');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await preCadastroService.atualizar(targetData.id, form);
      await onApprove(targetData.id);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao atualizar dados antes de aprovar.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    if (showLink) {
      navigator.clipboard.writeText(showLink);
      toast.success('Link copiado!');
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  if (!open) return null;

  return (
    <>
      {isMinimized && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              bottom: 16 + stackIndex * MINIMIZED_BAR_HEIGHT,
              right: 16,
              zIndex: dialogZIndex,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: theme.shadows[12],
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: 'background.paper',
            }}
          >
            <Box
              onClick={() => setIsMinimized(false)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 0.5,
                minHeight: 48,
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.primary.main, 0.15)
                  : alpha(theme.palette.primary.main, 0.08),
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <PersonAdd sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
              <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, color: 'text.primary' }}>
                {targetData ? `Revisão #${targetData.id}` : 'Novo Pré-Cadastro'}
              </Typography>
              <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()} sx={{ flexShrink: 0 }}>
                <Tooltip title="Restaurar">
                  <IconButton size="small" onClick={() => setIsMinimized(false)} sx={{ color: 'text.secondary' }}>
                    <OpenInFullIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Fechar">
                  <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) },
                    }}
                  >
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Portal>
      )}

      <ThemeProvider theme={embeddedTheme}>
        {!isMinimized && (
          <GlobalStyles
            styles={{
              '.MuiPopover-root': { zIndex: `${popoverZIndex} !important` },
              '.MuiMenu-root': { zIndex: `${popoverZIndex} !important` },
              '.MuiAutocomplete-popper': { zIndex: `${popoverZIndex} !important` },
            }}
          />
        )}
        <Dialog
          open={!isMinimized}
          onClose={handleClose}
          fullScreen={isMobile || maximized}
          maxWidth="md"
          fullWidth
          TransitionComponent={Slide}
          transitionDuration={250}
          sx={{ zIndex: dialogZIndex }}
          slotProps={{
            backdrop: {
              sx: {
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
              },
            },
          }}
          PaperProps={{
            sx: {
              margin: (isMobile || maximized) ? 0 : 1.5,
              maxHeight: (isMobile || maximized) ? '100vh' : '92vh',
              height: (isMobile || maximized) ? '100vh' : 'auto',
              borderRadius: (isMobile || maximized) ? 0 : 2,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 0.75,
              minHeight: 48,
              flexShrink: 0,
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.08),
              borderBottom: `1px solid ${theme.palette.divider}`,
              userSelect: 'none',
            }}
          >
            <PersonAdd sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
            <Box display="flex" alignItems="center" gap={1} flex={1}>
              <Typography variant="body1" fontWeight={600} noWrap sx={{ color: 'text.primary' }}>
                {targetData
                  ? `Revisão & Edição de Pré-Cadastro #${targetData.id.toString().padStart(5, '0')}`
                  : ticketId
                    ? 'Novo Pré-Cadastro pelo Atendimento'
                    : 'Novo Pré-Cadastro'}
              </Typography>
              {targetData?.status && <StatusChip status={targetData.status} />}
            </Box>

            <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
              <Tooltip title="Minimizar">
                <IconButton size="small" onClick={() => setIsMinimized(true)} sx={{ color: 'text.secondary' }}>
                  <MinimizeIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {!isMobile && (
                <Tooltip title={maximized ? 'Restaurar tamanho' : 'Maximizar'}>
                  <IconButton size="small" onClick={() => setMaximized(!maximized)} sx={{ color: 'text.secondary' }}>
                    {maximized ? <FullscreenExitIcon sx={{ fontSize: 20 }} /> : <FullscreenIcon sx={{ fontSize: 20 }} />}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Fechar">
                <IconButton
                  size="small"
                  onClick={handleClose}
                  disabled={loading}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'error.main', bgcolor: alpha(theme.palette.error.main, 0.08) },
                  }}
                >
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <DialogContent dividers sx={{ p: 2 }}>
            {fetchingDetails ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={2}>
                <CircularProgress size={36} />
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Carregando pré-cadastro #{effectiveId}...
                </Typography>
              </Box>
            ) : (
              <>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Link público do cliente existente (disponível apenas em Aguardando Assinatura ou Pendente) */}
                {targetData?.link_cliente && (targetData.status === 'aguardando_assinatura' || targetData.status === 'pendente') && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                      borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="primary"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        🔗 LINK PÚBLICO DO CLIENTE
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          wordBreak: 'break-all',
                          color: 'text.primary',
                          mt: 0.2,
                        }}
                      >
                        {targetData.link_cliente}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                        onClick={() => {
                          navigator.clipboard.writeText(targetData.link_cliente!);
                          toast.success('Link copiado!');
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', borderRadius: 1.5 }}
                      >
                        Copiar Link
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
                        onClick={() => window.open(targetData.link_cliente, '_blank')}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', borderRadius: 1.5 }}
                      >
                        Abrir Form
                      </Button>
                    </Box>
                  </Paper>
                )}

                {/* Link gerado */}
                {showLink && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600} gutterBottom>✅ Pré-cadastro criado! Envie este link para o cliente:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ wordBreak: 'break-all', flex: 1, fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.06)', p: 0.5, borderRadius: 1 }}>
                        {showLink}
                      </Typography>
                      <Tooltip title="Copiar link">
                        <IconButton size="small" onClick={handleCopyLink}><ContentCopy fontSize="small" /></IconButton>
                      </Tooltip>
                    </Box>
                  </Alert>
                )}

                <Grid container spacing={2}>
                  {/* Escritório */}
                  <Grid item xs={12} md={4}>
                    <EscritorioSelect
                      value={form.escritorio_id || null}
                      onChange={v => set('escritorio_id', v)}
                      label="Escritório"
                      size="small"
                      required
                      onlyActive
                      allowClear={false}
                      multiple={false}
                      error={!form.escritorio_id && !!error}
                    />
                  </Grid>

                  {/* Título do Contrato */}
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Título do Contrato"
                      value={form.titulo || ''}
                      onChange={e => set('titulo', e.target.value)}
                      error={!form.titulo?.trim() && !!error}
                    />
                  </Grid>

                  {/* Modelo de Contrato */}
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" required error={!form.contrato_template && !!error}>
                      <InputLabel>Modelo de Contrato</InputLabel>
                      <Select
                        value={form.contrato_template ?? ''}
                        onChange={(e) => set('contrato_template', e.target.value ? Number(e.target.value) : undefined)}
                        label="Modelo de Contrato"
                        disabled={contratoTemplatesLoading}
                        displayEmpty
                        renderValue={(v) => {
                          if (!v) return '';
                          const t = contratoTemplates.find((x) => x.id === v);
                          return t ? t.title : '';
                        }}
                      >
                        <MenuItem value="">
                          <em>Selecione um modelo</em>
                        </MenuItem>
                        {contratoTemplates.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Tipo de Pessoa e Cliente */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" required error={!form.tipo_pessoa && !!error}>
                      <InputLabel>Tipo de pessoa</InputLabel>
                      <Select
                        value={form.tipo_pessoa || 'pf'}
                        label="Tipo de pessoa"
                        onChange={e => {
                          const val = e.target.value as 'pf' | 'pj';
                          setForm(prev => ({
                            ...prev,
                            tipo_pessoa: val,
                            cpf_cnpj: '',
                            rg_ie: '',
                            nome_fantasia: '',
                          }));
                        }}
                      >
                        <MenuItem value="pf">Pessoa Física (PF)</MenuItem>
                        <MenuItem value="pj">Pessoa Jurídica (PJ)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={5}>
                    <FormControl fullWidth size="small" required error={!form.tipo_cliente && !!error}>
                      <InputLabel>Tipo de cliente</InputLabel>
                      <Select
                        value={form.tipo_cliente || ''}
                        label="Tipo de cliente"
                        onChange={e => set('tipo_cliente', e.target.value)}
                      >
                        {TIPO_CLIENTE_OPTIONS.map(o => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* ICMS e Comercial */}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small" required error={(form.contribuinte_icms === undefined || form.contribuinte_icms === null) && !!error}>
                      <InputLabel>Contribuinte ICMS</InputLabel>
                      <Select
                        value={form.contribuinte_icms ?? 9}
                        label="Contribuinte ICMS"
                        onChange={e => set('contribuinte_icms', Number(e.target.value))}
                      >
                        {CONTRIBUINTE_ICMS_OPTIONS.map(c => (
                          <MenuItem key={c.value} value={c.value}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small" required error={!form.como_conheceu && !!error}>
                      <InputLabel shrink>Como conheceu a Empresa?</InputLabel>
                      <Select
                        value={form.como_conheceu ?? ''}
                        label="Como conheceu a Empresa?"
                        displayEmpty
                        notched
                        onChange={e => {
                          const val = e.target.value;
                          set('como_conheceu', val);
                          if (val !== 'Indicação de amigo') {
                            set('indicado_por_id', null);
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>Selecione...</em>
                        </MenuItem>
                        {COMO_CONHECEU_OPTIONS.map(opcao => (
                          <MenuItem key={opcao} value={opcao}>
                            {opcao}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <ClientSelect
                      value={form.indicado_por_id || null}
                      onChange={(v: any) => set('indicado_por_id', v)}
                      label="Indicado por"
                      size="small"
                      required={form.como_conheceu === 'Indicação de amigo'}
                      placeholder="Busque o cliente que indicou"
                      disabled={form.como_conheceu !== 'Indicação de amigo'}
                      allowClear={true}
                      error={form.como_conheceu === 'Indicação de amigo' && !form.indicado_por_id && !!error}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FuncionariosSelect
                      value={form.vendedor_id || null}
                      onChange={(v: any) => set('vendedor_id', v)}
                      label="Vendedor"
                      size="small"
                      required
                      placeholder="Selecione o vendedor"
                      error={!form.vendedor_id && !!error}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  {/* ════════════════ 1. DADOS DO CLIENTE ════════════════ */}
                  <Grid item xs={12}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={0.5}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Person color="primary" sx={{ fontSize: 20 }} />
                        <Typography variant="subtitle2" fontWeight={700} color="primary">
                          1. DADOS DO CLIENTE
                        </Typography>
                      </Box>

                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={preenchidoPeloCliente}
                            onChange={(e) => setPreenchidoPeloCliente(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="caption" fontWeight={600} color="text.secondary">
                            Preenchido pelo cliente (link)
                          </Typography>
                        }
                      />
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                  </Grid>

                  {!preenchidoPeloCliente && (
                    <>
                      {form.tipo_pessoa === 'pj' ? (
                        <>
                          <Grid item xs={12} sm={6} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              required={!preenchidoPeloCliente}
                              label="Razão Social"
                              placeholder="Ex: Empresa Silva Serviços LTDA"
                              value={form.nome || ''}
                              onChange={e => set('nome', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              label="Nome Fantasia"
                              placeholder="Ex: Silva Conectividade"
                              value={form.nome_fantasia || ''}
                              onChange={e => set('nome_fantasia', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              required={!preenchidoPeloCliente}
                              label="CNPJ"
                              placeholder="00.000.000/0000-00"
                              value={form.cpf_cnpj || ''}
                              onChange={e => set('cpf_cnpj', formatarCNPJOnInput(e.target.value))}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              label="Inscrição Estadual"
                              placeholder="Ex: Isento ou Número"
                              value={form.rg_ie || ''}
                              onChange={e => set('rg_ie', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              type="date"
                              disabled={preenchidoPeloCliente}
                              label="Data de Abertura"
                              InputLabelProps={{ shrink: true }}
                              value={form.data_nascimento || ''}
                              onChange={e => set('data_nascimento', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              required={!preenchidoPeloCliente}
                              label="Telefone / Celular (WhatsApp)"
                              placeholder="(00) 00000-0000"
                              value={form.telefone || ''}
                              onChange={e => set('telefone', formatarPhoneOnInput(e.target.value))}
                            />
                          </Grid>

                          <Grid item xs={12} sm={12} md={12}>
                            <TextField
                              fullWidth
                              size="small"
                              type="email"
                              disabled={preenchidoPeloCliente}
                              label="E-mail"
                              placeholder="Ex: contato@empresa.com.br"
                              value={form.email || ''}
                              onChange={e => set('email', e.target.value)}
                            />
                          </Grid>
                        </>
                      ) : (
                        <>
                          <Grid item xs={12} sm={6} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              required={!preenchidoPeloCliente}
                              label="Nome Completo"
                              placeholder="Ex: Maria Oliveira Silva"
                              value={form.nome || ''}
                              onChange={e => set('nome', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              required={!preenchidoPeloCliente}
                              label="CPF"
                              placeholder="000.000.000-00"
                              value={form.cpf_cnpj || ''}
                              onChange={e => set('cpf_cnpj', formatarCPFOnInput(e.target.value))}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              label="RG (Identidade)"
                              placeholder="Ex: 12.345.678-9"
                              value={form.rg_ie || ''}
                              onChange={e => set('rg_ie', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              fullWidth
                              size="small"
                              type="date"
                              disabled={preenchidoPeloCliente}
                              required={!preenchidoPeloCliente}
                              label="Data de Nascimento"
                              InputLabelProps={{ shrink: true }}
                              value={form.data_nascimento || ''}
                              onChange={e => set('data_nascimento', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4.5}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              label="Nome da Mãe"
                              value={form.nome_mae || ''}
                              onChange={e => set('nome_mae', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4.5}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              label="Nome do Pai"
                              value={form.nome_pai || ''}
                              onChange={e => set('nome_pai', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={8}>
                            <TextField
                              fullWidth
                              size="small"
                              type="email"
                              disabled={preenchidoPeloCliente}
                              label="E-mail"
                              placeholder="Ex: seuemail@exemplo.com.br"
                              value={form.email || ''}
                              onChange={e => set('email', e.target.value)}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              disabled={preenchidoPeloCliente}
                              required={!preenchidoPeloCliente}
                              label="Telefone / Celular (WhatsApp)"
                              placeholder="(00) 00000-0000"
                              value={form.telefone || ''}
                              onChange={e => set('telefone', formatarPhoneOnInput(e.target.value))}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                      </Grid>

                      <Grid item xs={12} sm={4} md={2.5}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled={preenchidoPeloCliente}
                          required={!preenchidoPeloCliente}
                          label="CEP"
                          value={form.cep || ''}
                          onChange={e => {
                            const val = formatarCepOnInput(e.target.value);
                            set('cep', val);
                            if (val.replace(/\D/g, '').length === 8) {
                              handleBuscarCep(val);
                            }
                          }}
                          onBlur={() => {
                            if (form.cep && form.cep.replace(/\D/g, '').length === 8 && !cepBuscado) {
                              handleBuscarCep();
                            }
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleBuscarCep();
                            }
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Buscar CEP">
                                  <IconButton
                                    onClick={() => handleBuscarCep()}
                                    disabled={preenchidoPeloCliente || cepLoading || !form.cep || form.cep.replace(/\D/g, '').length < 8}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                  >
                                    {cepLoading ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <Search sx={{ fontSize: '1rem' }} />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={8} sm={5} md={5.5}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled={preenchidoPeloCliente || !cepBuscado}
                          required={!preenchidoPeloCliente}
                          label="Cidade"
                          value={form.cidade || ''}
                          onChange={e => set('cidade', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={4} sm={3} md={2}>
                        <EstadosBrSelect
                          value={form.estado || ''}
                          onChange={e => set('estado', e.target.value)}
                          disabled={preenchidoPeloCliente || !cepBuscado}
                          label="UF"
                          required={!preenchidoPeloCliente}
                          error={!form.estado && !(preenchidoPeloCliente || !cepBuscado) && !!error}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4} md={2}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled
                          label="Cód. IBGE"
                          value={form.codigo_municipio || ''}
                          onChange={e => set('codigo_municipio', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled={preenchidoPeloCliente || !cepBuscado}
                          required={!preenchidoPeloCliente}
                          label="Bairro"
                          value={form.bairro || ''}
                          onChange={e => set('bairro', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} sm={8} md={6.5}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled={preenchidoPeloCliente || !cepBuscado}
                          required={!preenchidoPeloCliente}
                          label="Endereço / Logradouro"
                          value={form.endereco || ''}
                          onChange={e => set('endereco', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={6} sm={4} md={1.5}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled={preenchidoPeloCliente || !cepBuscado}
                          label="Número"
                          value={form.numero || ''}
                          onChange={e => set('numero', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={6} sm={4} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled={preenchidoPeloCliente || !cepBuscado}
                          label="Complemento"
                          value={form.complemento || ''}
                          onChange={e => set('complemento', e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          disabled={preenchidoPeloCliente || !cepBuscado}
                          label="Ponto de Referência"
                          value={form.referencia || ''}
                          onChange={e => set('referencia', e.target.value)}
                        />
                      </Grid>
                    </>
                  )}

                  {/* ════════════════ 2. DADOS DO CONTRATO (ATENDENTE) ════════════════ */}
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <AttachMoney color="primary" sx={{ fontSize: 20 }} />
                      <Typography variant="subtitle2" fontWeight={700} color="primary">
                        2. DADOS DO CONTRATO
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                  </Grid>

                  {/* Autenticação & Rede */}
                  <Grid item xs={12} md={8}>
                    <PlanosSelect
                      value={form.plano_id || null}
                      onChange={(value: any) => {
                        const planoId = typeof value === 'number' ? value : null;
                        set('plano_id', planoId);
                        if (value && typeof value === 'number') {
                          planosService.getById(value).then(plan => {
                            if (plan) {
                              const acrescimo = Number(form.acrescimo) || 0;
                              const desconto = Number(form.desconto) || 0;
                              const planValor = typeof plan.valor === 'number' ? plan.valor : parseFloat(String(plan.valor)) || 0;
                              const valorFinal = Math.max(0, planValor + acrescimo - desconto);
                              setForm(prev => ({
                                ...prev,
                                plano_id: value,
                                valor_contratado: Number(valorFinal.toFixed(2)),
                              }));
                            }
                          }).catch(error => console.error('Erro ao buscar valor do plano:', error));
                        } else {
                          setForm(prev => ({
                            ...prev,
                            plano_id: null,
                            valor_contratado: undefined,
                          }));
                        }
                      }}
                      label="Plano"
                      size="small"
                      required
                      escritorio_id={form.escritorio_id}
                      error={!form.plano_id && !!error}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <FormControl fullWidth size="small" required error={!form.tipo_conexao && !!error}>
                      <InputLabel shrink>Tipo de Conexão</InputLabel>
                      <Select
                        value={form.tipo_conexao ?? ''}
                        label="Tipo de Conexão"
                        displayEmpty
                        notched
                        onChange={e => set('tipo_conexao', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Selecione...</em>
                        </MenuItem>
                        {TIPO_CONEXAO_OPTIONS.map(t => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4} md={4}>
                    <NasSelect
                      value={form.nas_id || null}
                      onChange={(v: any) => set('nas_id', v)}
                      label="Servidor NAS"
                      size="small"
                      required
                      error={!form.nas_id && !!error}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Login PPPoE"
                      value={form.pppoe_usuario || ''}
                      onChange={e => {
                        set('pppoe_usuario', e.target.value);
                        if (pppoeError) setPppoeError(null);
                      }}
                      onBlur={handleCheckPppoeUsuario}
                      error={!!pppoeError || (!form.pppoe_usuario && !!error)}
                      helperText={pppoeError}
                      InputProps={{
                        endAdornment: pppoeChecking ? (
                          <InputAdornment position="end">
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ) : null
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Senha PPPoE"
                      type={showPppoe ? 'text' : 'password'}
                      value={form.pppoe_senha || ''}
                      onChange={e => set('pppoe_senha', e.target.value)}
                      error={!form.pppoe_senha?.trim() && !!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPppoe(p => !p)}>
                              {showPppoe ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  {/* Taxas, Acréscimo, Desconto, Valor Contratado */}
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Taxa de Instalação"
                      placeholder="R$ 0,00"
                      value={formatCurrencyDisplay(form.taxa_instalacao)}
                      onChange={e => {
                        const inputValue = e.target.value;
                        if (!inputValue || inputValue.trim() === '') {
                          set('taxa_instalacao', 0);
                          return;
                        }
                        const formatted = formatCurrencyInput(inputValue);
                        const val = parseCurrencyInput(formatted);
                        set('taxa_instalacao', val);
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Acréscimo"
                      placeholder="R$ 0,00"
                      value={formatCurrencyDisplay(form.acrescimo)}
                      onChange={e => {
                        const inputValue = e.target.value;
                        const formatted = formatCurrencyInput(inputValue);
                        const acr = parseCurrencyInput(formatted);
                        setForm(prev => {
                          const desc = Number(prev.desconto) || 0;
                          const currentValor = Number(prev.valor_contratado) || 0;
                          const oldAcr = Number(prev.acrescimo) || 0;
                          const base = currentValor - oldAcr + desc;
                          const novoValor = Math.max(0, base + acr - desc);
                          return {
                            ...prev,
                            acrescimo: acr,
                            valor_contratado: Number(novoValor.toFixed(2)),
                          };
                        });
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Desconto"
                      placeholder="R$ 0,00"
                      value={formatCurrencyDisplay(form.desconto)}
                      onChange={e => {
                        const inputValue = e.target.value;
                        const formatted = formatCurrencyInput(inputValue);
                        const desc = parseCurrencyInput(formatted);
                        setForm(prev => {
                          const acr = Number(prev.acrescimo) || 0;
                          const currentValor = Number(prev.valor_contratado) || 0;
                          const oldDesc = Number(prev.desconto) || 0;
                          const base = currentValor - acr + oldDesc;
                          const novoValor = Math.max(0, base + acr - desc);
                          return {
                            ...prev,
                            desconto: desc,
                            valor_contratado: Number(novoValor.toFixed(2)),
                          };
                        });
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Valor Contratado"
                      placeholder="R$ 0,00"
                      value={formatCurrencyDisplay(form.valor_contratado)}
                      onChange={e => {
                        const inputValue = e.target.value;
                        if (!inputValue || inputValue.trim() === '') {
                          set('valor_contratado', 0);
                          return;
                        }
                        const formatted = formatCurrencyInput(inputValue);
                        const val = parseCurrencyInput(formatted);
                        set('valor_contratado', val);
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                    />
                  </Grid>
                  {/* CFOP */}
                  <Grid item xs={12} sm={6} md={4}>
                    <CfopSelect
                      value={form.cfop || '5307'}
                      onChange={(val) => set('cfop', val)}
                      size="small"
                      required
                    />
                  </Grid>

                  {/* Modalidade, Dia de Vencimento e Fidelidade */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" required error={!form.modalidade && !!error}>
                      <InputLabel shrink>Modalidade</InputLabel>
                      <Select
                        value={form.modalidade ?? ''}
                        label="Modalidade"
                        displayEmpty
                        notched
                        onChange={e => set('modalidade', e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Selecione a modalidade</em>
                        </MenuItem>
                        <MenuItem value="pos-pago">
                          Pós-pago{' '}
                          <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                            - Paga depois de usar
                          </Box>
                        </MenuItem>
                        <MenuItem value="pre-pago">
                          Pré-pago{' '}
                          <Box component="span" sx={{ fontStyle: 'italic', color: 'text.secondary', ml: 0.5 }}>
                            - Paga antes de usar
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6} sm={3} md={2}>
                    <DiasVencimentoSelect
                      value={form.dia_vencimento}
                      onChange={(v) => set('dia_vencimento', v === '' ? undefined : Number(v))}
                      error={!form.dia_vencimento && !!error}
                      allowClear
                      placeholder="Selecione um dia"
                    />
                  </Grid>

                  <Grid item xs={6} sm={3} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      required
                      label="Fidelidade (meses)"
                      type="number"
                      inputProps={{ min: 0, max: 60 }}
                      value={form.fidelidade ?? 12}
                      onChange={e => set('fidelidade', parseInt(e.target.value) || 0)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid item xs={12} sm={6} md={8}>
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="Data de Agendamento da Instalação"
                      InputLabelProps={{ shrink: true }}
                      value={form.data_instalacao_agendada || ''}
                      onChange={e => set('data_instalacao_agendada', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Observações"
                      multiline
                      rows={2}
                      value={form.observacoes || ''}
                      onChange={e => set('observacoes', e.target.value)}
                      placeholder="Observações internas sobre este pré-cadastro..."
                    />
                  </Grid>

                  {/* ════════════════ DOCUMENTOS & ASSINATURA DO CLIENTE ════════════════ */}
                  {targetData && (
                    (targetData.documentos && Object.keys(targetData.documentos).length > 0) ||
                    targetData.selfie_path ||
                    targetData.assinatura ||
                    targetData.assinado_em
                  ) && (
                      <>
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <Description color="primary" sx={{ fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={700} color="primary">
                              DOCUMENTOS E ASSINATURA DO CLIENTE
                            </Typography>
                          </Box>
                          <Divider sx={{ mb: 1.5 }} />
                        </Grid>

                        {/* Exibição dos Documentos */}
                        {(targetData.documentos || targetData.selfie_path) && (
                          <Grid item xs={12}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                              Documentos Anexados
                            </Typography>
                            <Grid container spacing={1.5}>
                              {targetData.selfie_path && (() => {
                                const imgUrl = targetData.selfie_path.startsWith('http')
                                  ? targetData.selfie_path
                                  : `${API_BASE}/${targetData.selfie_path.replace(/^\//, '')}`;
                                return (
                                  <Grid item xs={6} sm={4} md={3} key="selfie">
                                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 2 }}>
                                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={1}>
                                        <PhotoCamera color="primary" fontSize="small" />
                                        <Typography variant="caption" fontWeight={700}>Selfie c/ Doc</Typography>
                                      </Box>
                                      <Box component="img" src={imgUrl} alt="Selfie" sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<OpenInNew sx={{ fontSize: '0.9rem !important' }} />}
                                        onClick={() => window.open(imgUrl, '_blank')}
                                        fullWidth
                                        sx={{ fontSize: '0.75rem', py: 0.2 }}
                                      >
                                        Visualizar
                                      </Button>
                                    </Paper>
                                  </Grid>
                                );
                              })()}

                              {targetData.documentos && Object.entries(targetData.documentos).map(([key, rawPath]) => {
                                if (!rawPath) return null;
                                const docUrl = rawPath.startsWith('http')
                                  ? rawPath
                                  : `${API_BASE}/${rawPath.replace(/^\//, '')}`;
                                const isPdf = rawPath.toLowerCase().endsWith('.pdf');
                                const labelMap: Record<string, string> = {
                                  rg_frente: 'RG / CNH (Frente)',
                                  rg_verso: 'RG / CNH (Verso)',
                                  cpf: 'CPF / Cartão CNPJ',
                                  comprovante: 'Comprovante Residência',
                                };
                                const docLabel = labelMap[key] || key;

                                return (
                                  <Grid item xs={6} sm={4} md={3} key={key}>
                                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 2 }}>
                                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={1}>
                                        <FilePresent color="secondary" fontSize="small" />
                                        <Typography variant="caption" fontWeight={700} noWrap title={docLabel}>{docLabel}</Typography>
                                      </Box>
                                      {isPdf ? (
                                        <Box sx={{ height: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
                                          <InsertDriveFile color="action" sx={{ fontSize: 40 }} />
                                          <Typography variant="caption" color="text.secondary">Documento PDF</Typography>
                                        </Box>
                                      ) : (
                                        <Box component="img" src={docUrl} alt={docLabel} sx={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                                      )}
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<OpenInNew sx={{ fontSize: '0.9rem !important' }} />}
                                        onClick={() => window.open(docUrl, '_blank')}
                                        fullWidth
                                        sx={{ fontSize: '0.75rem', py: 0.2 }}
                                      >
                                        Visualizar
                                      </Button>
                                    </Paper>
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Grid>
                        )}

                        {/* Exibição da Assinatura Digital */}
                        {(targetData.assinatura || targetData.assinado_em) && (
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                              Assinatura Digital do Cliente
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                              <Grid container spacing={2} alignItems="center">
                                {targetData.assinatura && (
                                  <Grid item xs={12} sm={6} md={5}>
                                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, p: 1, bgcolor: '#ffffff', textAlign: 'center' }}>
                                      <Box
                                        component="img"
                                        src={targetData.assinatura.startsWith('data:') || targetData.assinatura.startsWith('http') ? targetData.assinatura : `${API_BASE}/${targetData.assinatura.replace(/^\//, '')}`}
                                        alt="Assinatura"
                                        sx={{ maxHeight: 90, maxWidth: '100%', objectFit: 'contain' }}
                                      />
                                    </Box>
                                  </Grid>
                                )}
                                <Grid item xs={12} sm={6} md={7}>
                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    {targetData.assinado_em && (
                                      <Typography variant="caption" color="text.secondary">
                                        <strong>Assinado em:</strong> {format(new Date(targetData.assinado_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                      </Typography>
                                    )}
                                    {targetData.ip_assinatura && (
                                      <Typography variant="caption" color="text.secondary">
                                        <strong>IP de Origem:</strong> {targetData.ip_assinatura}
                                      </Typography>
                                    )}
                                    {targetData.lat_lon_assinatura && (
                                      <Typography variant="caption" color="text.secondary">
                                        <strong>Coordenadas GPS:</strong> {targetData.lat_lon_assinatura}
                                      </Typography>
                                    )}
                                    {targetData.user_agent && (
                                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                        <strong>Dispositivo:</strong> {targetData.user_agent}
                                      </Typography>
                                    )}
                                  </Box>
                                </Grid>
                              </Grid>
                            </Paper>
                          </Grid>
                        )}
                      </>
                    )}

                  {/* Documentos do cliente */}
                  {targetData && (() => {
                    const docs = targetData.documentos || {};
                    if (docs.rg_frente || docs.rg_verso || docs.cpf || docs.comprovante || targetData.selfie_path) {
                      return (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary">
                            📎 DOCUMENTOS ENVIADOS PELO CLIENTE
                          </Typography>
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {[
                              ['RG Frente', docs.rg_frente],
                              ['RG Verso', docs.rg_verso],
                              ['CPF', docs.cpf],
                              ['Comprovante', docs.comprovante],
                              ['Selfie com doc', targetData.selfie_path],
                            ].map(([label, path]) =>
                              path ? (
                                <Tooltip key={label as string} title={`Visualizar ${label}`}>
                                  <Chip
                                    icon={<AttachFile sx={{ fontSize: 16 }} />}
                                    label={label as string}
                                    size="small"
                                    clickable
                                    onClick={() => window.open(`${API_BASE}${path}`, '_blank')}
                                    color="primary"
                                    variant="outlined"
                                  />
                                </Tooltip>
                              ) : null
                            )}
                          </Box>
                        </Grid>
                      );
                    }
                    return null;
                  })()}

                  {/* Assinatura */}
                  {targetData?.assinado_em && (
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ fontSize: '0.78rem' }}>
                        ✍️ <strong>Assinado eletronicamente pelo cliente em:</strong>{' '}
                        {format(new Date(targetData.assinado_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </Alert>
                    </Grid>
                  )}

                  {/* Motivo de reprovação */}
                  {targetData?.motivo_reprovacao && (
                    <Grid item xs={12}>
                      <Alert severity="error">
                        <Typography variant="caption" fontWeight={700}>
                          Motivo da reprovação:
                        </Typography>
                        <Typography variant="body2">{targetData.motivo_reprovacao}</Typography>
                      </Alert>
                    </Grid>
                  )}

                  {/* IDs gerados */}
                  {targetData?.status === 'aprovado' && (targetData.cliente_id_gerado || targetData.contrato_id_gerado) && (
                    <Grid item xs={12}>
                      <Alert severity="success">
                        <Typography variant="caption">
                          ✅ Cliente criado: <strong>#{targetData.cliente_id_gerado}</strong> | Contrato:{' '}
                          <strong>#{targetData.contrato_id_gerado}</strong>
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 1.5, gap: 1, justifyContent: targetData ? 'space-between' : 'flex-end' }}>
            {targetData ? (
              <>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </Button>

                <Box display="flex" gap={1}>
                  <Button onClick={handleClose} color="inherit" size="small" disabled={loading}>
                    Fechar
                  </Button>

                  {(targetData.status === 'aguardando_aprovacao' || targetData.status === 'pendente' || targetData.status === 'aguardando_assinatura') && onReject && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<Cancel />}
                      onClick={() => onReject(targetData.id)}
                      disabled={loading}
                    >
                      Reprovar
                    </Button>
                  )}

                  {targetData.status === 'aguardando_aprovacao' && onApprove && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={handleApproveWithValidation}
                      disabled={loading}
                    >
                      Aprovar
                    </Button>
                  )}

                  {(targetData.status === 'pendente' || targetData.status === 'aguardando_assinatura') && onApprove && (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={handleApproveWithValidation}
                      disabled={loading}
                    >
                      Gerar Cadastro/Contrato
                    </Button>
                  )}

                  {targetData.status === 'aprovado' && targetData.cliente_id_gerado && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNew />}
                      href={`/clients/${targetData.cliente_id_gerado}`}
                    >
                      Ver Cliente
                    </Button>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Button onClick={handleClose} disabled={loading} color="inherit">Cancelar</Button>
                {!showLink ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <Send />}
                  >
                    {loading ? 'Criando...' : (ticketId ? 'Criar e enviar link' : 'Criar e Gerar Link')}
                  </Button>
                ) : (
                  <Button variant="outlined" onClick={handleCopyLink} startIcon={<ContentCopy />}>
                    Copiar Link
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </>
  );
};

export default PreCadastroForm;
